import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { StructuredData } from "@/components/StructuredData";
import { ProtectedLayout } from "@/components/layout/ProtectedLayout";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/contexts/AuthContext";
import { usePageTracking } from "@/hooks/useAnalytics";
import { ComponentType, lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

// ─── LAZY WITH RETRY (auto-reload on stale chunk after deploy) ──────────────

function lazyWithRetry(
  factory: () => Promise<{ default: ComponentType<unknown> }>,
) {
  return lazy(() =>
    factory().catch((error: unknown) => {
      const hasReloaded = sessionStorage.getItem("chunk_reload");
      if (!hasReloaded) {
        sessionStorage.setItem("chunk_reload", "1");
        window.location.reload();
      }
      throw error;
    }),
  );
}

// ─── LAZY-LOADED PAGES (code splitting) ─────────────────────────────────────

const AgendaPage = lazyWithRetry(() => import("@/pages/AgendaPage"));
const AnamnesisPage = lazyWithRetry(() => import("@/pages/AnamnesisPage"));
const AtendimentoPage = lazyWithRetry(() => import("@/pages/AtendimentoPage"));
const CompanyPortalPage = lazyWithRetry(
  () => import("@/pages/CompanyPortalPage"),
);
const ContatoPage = lazyWithRetry(() => import("@/pages/ContatoPage"));
const ContractsPage = lazyWithRetry(() => import("@/pages/ContractsPage"));
const DashboardPage = lazyWithRetry(() => import("@/pages/DashboardPage"));
const DocumentsPage = lazyWithRetry(() => import("@/pages/DocumentsPage"));
const EmpresasPage = lazyWithRetry(() => import("@/pages/EmpresasPage"));
const FinancialPage = lazyWithRetry(() => import("@/pages/FinancialPage"));
const ForgotPasswordPage = lazyWithRetry(
  () => import("@/pages/ForgotPasswordPage"),
);
const HomePage = lazyWithRetry(() => import("@/pages/HomePage"));
const InstitutoPage = lazyWithRetry(() => import("@/pages/InstitutoPage"));
const LaudoPage = lazyWithRetry(() => import("@/pages/LaudoPage"));
const LoginPage = lazyWithRetry(() => import("@/pages/LoginPage"));
const NotificationsPage = lazyWithRetry(
  () => import("@/pages/NotificationsPage"),
);
const Nr1DashboardPage = lazyWithRetry(
  () => import("@/pages/Nr1DashboardPage"),
);
const PatientDetailPage = lazyWithRetry(
  () => import("@/pages/PatientDetailPage"),
);
const PatientPortalPage = lazyWithRetry(
  () => import("@/pages/PatientPortalPageV2"),
);
const PatientsPage = lazyWithRetry(() => import("@/pages/PatientsPage"));
const ProductivityPage = lazyWithRetry(
  () => import("@/pages/ProductivityPage"),
);
const QuestionnaireBuilderPage = lazyWithRetry(
  () => import("@/pages/QuestionnaireBuilderPage"),
);
const RecordsPage = lazyWithRetry(() => import("@/pages/RecordsPage"));
const ResetPasswordPage = lazyWithRetry(
  () => import("@/pages/ResetPasswordPage"),
);
const ScalesPage = lazyWithRetry(() => import("@/pages/ScalesPage"));
const CompaniesPage = lazyWithRetry(
  () => import("@/pages/admin/CompaniesPage"),
);
const CompanyDetailPage = lazyWithRetry(
  () => import("@/pages/admin/CompanyDetailPage"),
);
const UsersPage = lazyWithRetry(() => import("@/pages/admin/UsersPage"));
const PublicFormPage = lazyWithRetry(
  () => import("@/pages/public/PublicFormPage"),
);
const PublicNr1FormPage = lazyWithRetry(
  () => import("@/pages/public/PublicNr1FormPage"),
);
const QuestionnairePage = lazyWithRetry(
  () => import("@/pages/public/QuestionnairePage"),
);

function PageLoadingFallback() {
  return (
    <div
      className="flex min-h-screen items-center justify-center"
      role="status"
      aria-label="Carregando página"
    >
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <PageTracker />
      <StructuredData />
      <ScrollToTop />
      <AuthProvider>
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/instituto" element={<InstitutoPage />} />
            <Route path="/atendimento" element={<AtendimentoPage />} />
            <Route path="/empresas" element={<EmpresasPage />} />
            <Route path="/contato" element={<ContatoPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/questionario/:token"
              element={<QuestionnairePage />}
            />
            <Route path="/formulario/:token" element={<PublicFormPage />} />
            <Route path="/nr1/:token" element={<PublicNr1FormPage />} />

            {/* Company portal (own layout) */}
            <Route
              path="/portal"
              element={
                <ProtectedRoute allowedRoles={["EMPRESA"]}>
                  <CompanyPortalPage />
                </ProtectedRoute>
              }
            />

            {/* Protected routes with sidebar layout */}
            <Route element={<ProtectedLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route
                path="/agenda"
                element={
                  <ProtectedRoute
                    allowedRoles={["ADMIN", "PSICOLOGA", "RECEPCAO"]}
                  >
                    <AgendaPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pacientes"
                element={
                  <ProtectedRoute
                    allowedRoles={["ADMIN", "PSICOLOGA", "RECEPCAO"]}
                  >
                    <PatientsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pacientes/:id"
                element={
                  <ProtectedRoute
                    allowedRoles={["ADMIN", "PSICOLOGA", "RECEPCAO"]}
                  >
                    <PatientDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pacientes/:patientId/anamnese"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <AnamnesisPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/prontuario"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <RecordsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/laudo"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <LaudoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documentos"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <DocumentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <FinancialPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/escalas"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <ScalesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/questionarios/criar"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <QuestionnaireBuilderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nr1"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <Nr1DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contratos"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <ContractsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notificacoes"
                element={
                  <ProtectedRoute
                    allowedRoles={["ADMIN", "PSICOLOGA", "RECEPCAO"]}
                  >
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/produtividade"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <ProductivityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meu-espaco"
                element={
                  <ProtectedRoute allowedRoles={["PACIENTE", "FAMILIAR"]}>
                    <PatientPortalPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/usuarios"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/empresas"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <CompaniesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/empresas/:id"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PSICOLOGA"]}>
                    <CompanyDetailPage />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </Suspense>
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
